// Shared helpers of the reminder settings screens. Times are shown in the reminder's
// own time zone (what its schedule runs on), whatever the viewer's browser says.

export const DEFAULT_TIME_ZONE = 'Asia/Ho_Chi_Minh';

// The viewer's own zone, for the "your browser" side of the clock.
export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const formatInZone = (value, timeZone = DEFAULT_TIME_ZONE, withWeekday = false) => (value
  ? new Date(value).toLocaleString('en-GB', {
    ...(withWeekday ? { weekday: 'short' } : {}),
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone,
  })
  : '—');

// "GMT+7" for any IANA name; '' when the browser does not know the zone.
export const zoneOffsetLabel = (timeZone, at = new Date()) => {
  try {
    const part = new Intl.DateTimeFormat('en-GB', { timeZone, timeZoneName: 'shortOffset' })
      .formatToParts(at).find((p) => p.type === 'timeZoneName');
    return part ? part.value : '';
  } catch {
    return '';
  }
};

export const slotLabel = (definition, slotKey) =>
  definition?.slots?.find((s) => s.key === slotKey)?.label || slotKey;

// Ruby wday: 0 = Sunday. Shown Monday first.
export const DAYS = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' }, { value: 0, label: 'Sun' },
];

// Days of the month a monthly reminder/task may run on (backend MONTH_DAYS: every
// month has them).
export const MONTH_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

export const isTask = (definition) => definition?.kind === 'task';
export const isMonthly = (definition) => definition?.cadence === 'monthly';

// Server settings → form state. `users` fields become plain emails (RecipientSelect).
export const toForm = (definition, settings) => ({
  enabled: !!settings.enabled,
  time_zone: settings.time_zone || DEFAULT_TIME_ZONE,
  days: [...settings.days],
  ...(settings.month_days ? { month_days: [...settings.month_days] } : {}),
  times: { ...settings.times },
  options: Object.fromEntries(definition.fields.map((field) => {
    const value = settings.options[field.key];
    return [field.key, field.type === 'users' ? (value || []).map((u) => u.email) : value];
  })),
});

// Form state → PUT body. Days sorted like the server keeps them, so re-ticking a
// day never reads as a change.
export const toPayload = (form) => ({
  ...form,
  days: [...form.days].sort((a, b) => a - b),
  ...(form.month_days ? { month_days: [...form.month_days].sort((a, b) => a - b) } : {}),
});

// A cleared number box or a missing required time cannot be saved.
export const isIncomplete = (definition, form) =>
  (isMonthly(definition) && !(form.month_days || []).length) ||
  definition.fields.some((f) => f.type === 'integer' && form.options[f.key] === '') ||
  definition.slots.some((s) => !s.optional && !form.times[s.key]);
